import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Badge, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import apiService from '../../services/api';

const ProductManagement = ({ organization, onBack }) => {
    const [items, setItems] = useState([]);
    const [solutions, setSolutions] = useState([]);
    const [brands, setBrands] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchName, setSearchName] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);

    const emptyForm = {
        solution_id: '',
        brand_id: '',
        currency_id: '',
        name: '',
        description: '',
        cost_price: 0,
        selling_price: 0,
        sales_rep_commission: 0,
        model_code: '',
        ordinal: 0,
        is_active: true
    };
    const [formData, setFormData] = useState(emptyForm);

    useEffect(() => {
        if (organization) {
            loadAll();
        }
    }, [organization]);

    const loadAll = async () => {
        setLoading(true);
        setError('');
        try {
            const [productsRes, solutionsRes, brandsRes, currenciesRes] = await Promise.all([
                apiService.getProductsByOrganization(organization.id),
                apiService.get('/solution'),
                apiService.getBrandsByOrganization(organization.id),
                apiService.getCurrencies()
            ]);

            if (productsRes.success) {
                setItems(productsRes.data || []);
            } else {
                setError(productsRes.error || 'Failed to load products');
                setItems([]);
            }

            setSolutions(solutionsRes?.data || []);
            setBrands(brandsRes?.success ? (brandsRes.data || []) : []);
            setCurrencies(currenciesRes?.success ? (currenciesRes.data || []) : []);
        } catch (err) {
            console.error('Error loading products:', err);
            setError('Failed to load products');
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getFilteredAndSorted = () => {
        let filtered = items.filter(item => {
            return !searchName || (item.name && item.name.toLowerCase().includes(searchName.toLowerCase()));
        });

        if (!sortConfig.key) return filtered;

        return [...filtered].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? '';
            let bVal = b[sortConfig.key] ?? '';
            if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortConfig.direction === 'asc' ?
            <i className="bi bi-arrow-up ms-1"></i> :
            <i className="bi bi-arrow-down ms-1"></i>;
    };

    const formatMoney = (val) => {
        if (val === null || val === undefined || val === '') return '-';
        const num = Number(val);
        if (Number.isNaN(num)) return '-';
        return num.toFixed(2);
    };

    const getSolutionName = (id) => solutions.find(s => s.id === id)?.name || '-';
    const getBrandName = (id) => brands.find(b => b.id === id)?.name || '-';
    const getCurrencyName = (id) => currencies.find(c => c.id === id)?.name || '-';

    const handleShowModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                solution_id: item.solution_id ?? '',
                brand_id: item.brand_id ?? '',
                currency_id: item.currency_id ?? '',
                name: item.name || '',
                description: item.description || '',
                cost_price: item.cost_price ?? 0,
                selling_price: item.selling_price ?? 0,
                sales_rep_commission: item.sales_rep_commission ?? 0,
                model_code: item.model_code || '',
                ordinal: item.ordinal ?? 0,
                is_active: item.is_active ?? true
            });
        } else {
            setEditingItem(null);
            setFormData(emptyForm);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData(emptyForm);
        setError('');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) { setError('Name is required'); return; }
        if (!formData.solution_id) { setError('Solution is required'); return; }
        if (!formData.brand_id) { setError('Brand is required'); return; }
        if (!formData.currency_id) { setError('Currency is required'); return; }

        setSaving(true);
        setError('');

        try {
            const data = {
                organization_id: organization.id,
                solution_id: parseInt(formData.solution_id, 10),
                brand_id: parseInt(formData.brand_id, 10),
                currency_id: parseInt(formData.currency_id, 10),
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                cost_price: parseFloat(formData.cost_price) || 0,
                selling_price: parseFloat(formData.selling_price) || 0,
                sales_rep_commission: parseFloat(formData.sales_rep_commission) || 0,
                model_code: formData.model_code?.trim() || '',
                ordinal: parseInt(formData.ordinal, 10) || 0,
                is_active: formData.is_active
            };

            let response;
            if (editingItem) {
                response = await apiService.updateProduct(editingItem.id, data);
            } else {
                response = await apiService.createProduct(data);
            }

            if (response.success) {
                handleCloseModal();
                await loadAll();
            } else {
                setError(response.error || 'Failed to save product');
            }
        } catch (err) {
            console.error('Error saving product:', err);
            setError('Failed to save product');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <Button variant="outline-secondary" onClick={onBack} className="me-3">
                        <i className="bi bi-arrow-left me-1"></i> Back
                    </Button>
                    <span className="fs-4 fw-bold">
                        <i className="bi bi-box-seam me-2"></i>Product Management
                    </span>
                    <p className="text-muted mt-2 mb-0">Manage products for {organization.name}</p>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </Alert>
            )}

            <Card className="shadow-sm">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="bi bi-list-check me-2"></i>Products
                        </h5>
                        <div className="d-flex align-items-center">
                            {!loading && items.length > 0 && (
                                <Badge bg="info" className="me-3">{getFilteredAndSorted().length} / {items.length}</Badge>
                            )}
                            <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
                                <i className="bi bi-plus-circle me-1"></i> Add Product
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" /><p className="mt-3 text-muted">Loading products...</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-box-seam display-1"></i>
                            <div className="mt-2">No products found for this organization</div>
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold"><i className="bi bi-search me-1"></i>Search by Name</Form.Label>
                                        <Form.Control type="text" placeholder="Enter name..." value={searchName} onChange={(e) => setSearchName(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            {searchName && (
                                <div className="mb-3">
                                    <Button variant="outline-secondary" size="sm" onClick={() => setSearchName('')}>
                                        <i className="bi bi-x-circle me-1"></i>Clear Filters
                                    </Button>
                                </div>
                            )}
                            <Table striped bordered hover responsive>
                                <thead className="table-dark">
                                    <tr>
                                        <th onClick={() => handleSort('solution_id')} style={{ cursor: 'pointer' }}>Solution {getSortIcon('solution_id')}</th>
                                        <th onClick={() => handleSort('brand_id')} style={{ cursor: 'pointer' }}>Brand {getSortIcon('brand_id')}</th>
                                        <th onClick={() => handleSort('currency_id')} style={{ cursor: 'pointer' }}>Currency {getSortIcon('currency_id')}</th>
                                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name {getSortIcon('name')}</th>
                                        <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>Description {getSortIcon('description')}</th>
                                        <th onClick={() => handleSort('cost_price')} style={{ cursor: 'pointer' }} className="text-end">Cost Price {getSortIcon('cost_price')}</th>
                                        <th onClick={() => handleSort('selling_price')} style={{ cursor: 'pointer' }} className="text-end">Selling Price {getSortIcon('selling_price')}</th>
                                        <th onClick={() => handleSort('sales_rep_commission')} style={{ cursor: 'pointer' }} className="text-end">Sales Rep Commission {getSortIcon('sales_rep_commission')}</th>
                                        <th onClick={() => handleSort('model_code')} style={{ cursor: 'pointer' }}>Model Code {getSortIcon('model_code')}</th>
                                        <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer' }}>Active {getSortIcon('is_active')}</th>
                                        <th onClick={() => handleSort('ordinal')} style={{ cursor: 'pointer' }}>Ordinal {getSortIcon('ordinal')}</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getFilteredAndSorted().map(item => (
                                        <tr key={item.id}>
                                            <td>{getSolutionName(item.solution_id)}</td>
                                            <td>{getBrandName(item.brand_id)}</td>
                                            <td>{getCurrencyName(item.currency_id)}</td>
                                            <td>{item.name || '-'}</td>
                                            <td>{item.description || '-'}</td>
                                            <td className="text-end">{formatMoney(item.cost_price)}</td>
                                            <td className="text-end">{formatMoney(item.selling_price)}</td>
                                            <td className="text-end">{formatMoney(item.sales_rep_commission)}</td>
                                            <td>{item.model_code || '-'}</td>
                                            <td><Badge bg={item.is_active ? 'success' : 'secondary'}>{item.is_active ? 'Yes' : 'No'}</Badge></td>
                                            <td>{item.ordinal ?? '-'}</td>
                                            <td>
                                                <Button variant="outline-primary" size="sm" title="Edit" onClick={() => handleShowModal(item)}>
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Product' : 'Add Product'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        {error && <Alert variant="danger" dismissible onClose={() => setError('')}><i className="bi bi-exclamation-triangle me-2"></i>{error}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>Organization</Form.Label>
                            <Form.Control type="text" value={organization?.name || ''} disabled readOnly />
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Solution *</Form.Label>
                                    <Form.Select name="solution_id" value={formData.solution_id} onChange={handleInputChange} required>
                                        <option value="">-- Select Solution --</option>
                                        {solutions.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Brand *</Form.Label>
                                    <Form.Select name="brand_id" value={formData.brand_id} onChange={handleInputChange} required>
                                        <option value="">-- Select Brand --</option>
                                        {brands.map(b => (
                                            <option key={b.id} value={b.id}>{b.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Currency *</Form.Label>
                                    <Form.Select name="currency_id" value={formData.currency_id} onChange={handleInputChange} required>
                                        <option value="">-- Select Currency --</option>
                                        {currencies.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter product name" required maxLength={255} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Model Code</Form.Label>
                                    <Form.Control type="text" name="model_code" value={formData.model_code} onChange={handleInputChange} placeholder="Enter model code" maxLength={255} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleInputChange} placeholder="Enter description" />
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Cost Price</Form.Label>
                                    <Form.Control type="number" step="0.01" min={0} name="cost_price" value={formData.cost_price} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Selling Price</Form.Label>
                                    <Form.Control type="number" step="0.01" min={0} name="selling_price" value={formData.selling_price} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sales Rep Commission</Form.Label>
                                    <Form.Control type="number" step="0.01" min={0} name="sales_rep_commission" value={formData.sales_rep_commission} onChange={handleInputChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ordinal</Form.Label>
                                    <Form.Control type="number" name="ordinal" value={formData.ordinal} onChange={handleInputChange} min={0} />
                                </Form.Group>
                            </Col>
                            <Col md={4} className="d-flex align-items-center">
                                <Form.Group className="mb-3">
                                    <Form.Check type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} label="Active" />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default ProductManagement;
